import { Box, Button, Flex, HStack, Image, Text, Tooltip, useClipboard, useColorModeValue } from '@chakra-ui/react';
import pickBy from 'lodash/pickBy';
import { useRouter } from 'next/router';
import React from 'react';

import type { EntityTag } from 'ui/shared/EntityTags/types';

import config from 'configs/app';
import getCheckedSummedAddress from 'lib/address/getCheckedSummedAddress';
import useAddressMetadataInfoQuery from 'lib/address/useAddressMetadataInfoQuery';
import useApiQuery from 'lib/api/useApiQuery';
import useAddressProfileApiQuery from 'lib/hooks/useAddressProfileApiQuery';
import useIsSafeAddress from 'lib/hooks/useIsSafeAddress';
import { useI18n } from 'lib/i18n/context';
import getNetworkValidationActionText from 'lib/networks/getNetworkValidationActionText';
import getQueryParamString from 'lib/router/getQueryParamString';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import useFetchXStarScore from 'lib/xStarScore/useFetchXStarScore';
import { ADDRESS_TABS_COUNTERS } from 'stubs/address';
import { USER_OPS_ACCOUNT } from 'stubs/userOps';
import AddressAccountHistory from 'ui/address/AddressAccountHistory';
import AddressBlocksValidated from 'ui/address/AddressBlocksValidated';
import AddressCoinBalance from 'ui/address/AddressCoinBalance';
import AddressContract from 'ui/address/AddressContract';
import AddressDetails from 'ui/address/AddressDetails';
import AddressEpochRewards from 'ui/address/AddressEpochRewards';
import AddressInternalTxs from 'ui/address/AddressInternalTxs';
import AddressLogs from 'ui/address/AddressLogs';
import AddressMud from 'ui/address/AddressMud';
import AddressTokens from 'ui/address/AddressTokens';
import AddressTokenTransfers from 'ui/address/AddressTokenTransfers';
import AddressTxs from 'ui/address/AddressTxs';
import AddressUserOps from 'ui/address/AddressUserOps';
import AddressWithdrawals from 'ui/address/AddressWithdrawals';
import useContractTabs from 'ui/address/contract/useContractTabs';
import { CONTRACT_TAB_IDS } from 'ui/address/contract/utils';
import AddressFavoriteButton from 'ui/address/details/AddressFavoriteButton';
import AddressMetadataAlert from 'ui/address/details/AddressMetadataAlert';
import AddressQrCode from 'ui/address/details/AddressQrCode';
import AddressEnsDomains from 'ui/address/ensDomains/AddressEnsDomains';
import { ADDRESS_PAGE_KEYS, ADDRESS_TAB_KEYS, ADDRESS_TAG_KEYS } from 'ui/address/i18n';
import SolidityscanReport from 'ui/address/SolidityscanReport';
import useAddressQuery from 'ui/address/utils/useAddressQuery';
import useCheckAddressFormat from 'ui/address/utils/useCheckAddressFormat';
import useCheckDomainNameParam from 'ui/address/utils/useCheckDomainNameParam';
import AccountActionsMenu from 'ui/shared/AccountActionsMenu/AccountActionsMenu';
import AddressAddToWallet from 'ui/shared/address/AddressAddToWallet';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import EnsEntity from 'ui/shared/entities/ens/EnsEntity';
import EntityTags from 'ui/shared/EntityTags/EntityTags';
import formatUserTags from 'ui/shared/EntityTags/formatUserTags';
import sortEntityTags from 'ui/shared/EntityTags/sortEntityTags';
import NetworkExplorers from 'ui/shared/NetworkExplorers';

const TOKEN_TABS = [ 'tokens_erc20', 'tokens_nfts', 'tokens_nfts_collection', 'tokens_nfts_list' ];
const PREDEFINED_TAG_PRIORITY = 100;

const txInterpretation = config.features.txInterpretation;
const addressProfileAPIFeature = config.features.addressProfileAPI;
const xScoreFeature = config.features.xStarScore;

const AddressCopyButton = ({ text, isLoading }: { text: string; isLoading?: boolean }) => {
  const { hasCopied, onCopy } = useClipboard(text, 1000);
  const { t } = useI18n();
  const handleClick = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onCopy();
  }, [ onCopy ]);

  if (isLoading) {
    return null;
  }

  return (
    <Tooltip label={ hasCopied ? t(ADDRESS_PAGE_KEYS.actions.copied) : t(ADDRESS_PAGE_KEYS.actions.copyToClipboard) } color="white">
      <Box
        as="button"
        type="button"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize="16px"
        flexShrink={ 0 }
        onClick={ handleClick }
      >
        <Image src="/static/address/address-copy.svg" alt="" boxSize="16px"/>
      </Box>
    </Tooltip>
  );
};

const AddressPageContent = () => {
  const router = useRouter();
  const { t } = useI18n();

  const tabsScrollRef = React.useRef<HTMLDivElement>(null);
  const hash = getQueryParamString(router.query.hash);
  const checkSummedHash = React.useMemo(() => getCheckedSummedAddress(hash), [ hash ]);

  const checkDomainName = useCheckDomainNameParam(hash);
  const checkAddressFormat = useCheckAddressFormat(hash);
  const areQueriesEnabled = !checkDomainName && !checkAddressFormat;
  const addressQuery = useAddressQuery({ hash, isEnabled: areQueriesEnabled });

  const addressTabsCountersQuery = useApiQuery('address_tabs_counters', {
    pathParams: { hash },
    queryOptions: {
      enabled: areQueriesEnabled && Boolean(hash),
      placeholderData: ADDRESS_TABS_COUNTERS,
    },
  });

  const userOpsAccountQuery = useApiQuery('user_ops_account', {
    pathParams: { hash },
    queryOptions: {
      enabled: areQueriesEnabled && Boolean(hash) && config.features.userOps.isEnabled,
      placeholderData: USER_OPS_ACCOUNT,
    },
  });

  const mudTablesCountQuery = useApiQuery('address_mud_tables_count', {
    pathParams: { hash },
    queryOptions: {
      enabled: config.features.mudFramework.isEnabled && areQueriesEnabled && Boolean(hash),
      placeholderData: 10,
    },
  });

  const addressesForMetadataQuery = React.useMemo(() => ([ hash ].filter(Boolean)), [ hash ]);
  const addressMetadataQuery = useAddressMetadataInfoQuery(addressesForMetadataQuery, areQueriesEnabled);
  const userPropfileApiQuery = useAddressProfileApiQuery(hash, addressProfileAPIFeature.isEnabled && areQueriesEnabled);

  const addressEnsDomainsQuery = useApiQuery('addresses_lookup', {
    pathParams: { chainId: config.chain.id },
    queryParams: {
      address: hash,
      resolved_to: true,
      owned_by: true,
      only_active: true,
      order: 'ASC',
    },
    queryOptions: {
      enabled: Boolean(hash) && config.features.nameService.isEnabled,
    },
  });
  const addressMainDomain = !addressQuery.isPlaceholderData ?
    addressEnsDomainsQuery.data?.items.find((domain) => domain.name === addressQuery.data?.ens_domain_name) :
    undefined;

  const isLoading = addressQuery.isPlaceholderData;
  const isTabsLoading =
    isLoading ||
    addressTabsCountersQuery.isPlaceholderData ||
    (config.features.userOps.isEnabled && userOpsAccountQuery.isPlaceholderData) ||
    (config.features.mudFramework.isEnabled && mudTablesCountQuery.isPlaceholderData);

  const handleFetchedBytecodeMessage = React.useCallback(() => {
    addressQuery.refetch();
  }, [ addressQuery ]);

  const channel = useSocketChannel({
    topic: `addresses:${ hash?.toLowerCase() }`,
    isDisabled: isTabsLoading || addressQuery.isDegradedData || Boolean(addressQuery.data?.is_contract),
  });
  useSocketMessage({
    channel,
    event: 'fetched_bytecode',
    handler: handleFetchedBytecodeMessage,
  });

  const isSafeAddress = useIsSafeAddress(!addressQuery.isPlaceholderData && addressQuery.data?.is_contract ? hash : undefined);
  const safeIconColor = useColorModeValue('black', 'white');

  const xStarQuery = useFetchXStarScore({ hash });

  const contractTabs = useContractTabs(
    addressQuery.data,
    config.features.mudFramework.isEnabled ? (mudTablesCountQuery.isPlaceholderData || addressQuery.isPlaceholderData) : addressQuery.isPlaceholderData,
    Boolean(config.features.mudFramework.isEnabled && mudTablesCountQuery.data && mudTablesCountQuery.data > 0),
  );

  const tabs = React.useMemo(() => {
    return [
      config.features.mudFramework.isEnabled && mudTablesCountQuery.data && mudTablesCountQuery.data > 0 && {
        id: 'mud',
        title: t(ADDRESS_TAB_KEYS.mud),
        count: mudTablesCountQuery.data,
        subTabs: undefined as Array<string> | undefined,
      },
      {
        id: 'txs',
        title: t(ADDRESS_TAB_KEYS.transactions),
        count: addressTabsCountersQuery.data?.transactions_count,
        subTabs: undefined as Array<string> | undefined,
      },
      txInterpretation.isEnabled && txInterpretation.provider === 'noves' ?
        { id: 'account_history', title: t(ADDRESS_TAB_KEYS.accountHistory), count: undefined, subTabs: undefined as Array<string> | undefined } :
        undefined,
      config.features.userOps.isEnabled && Boolean(userOpsAccountQuery.data?.total_ops) ?
        { id: 'user_ops', title: t(ADDRESS_TAB_KEYS.userOps), count: userOpsAccountQuery.data?.total_ops, subTabs: undefined as Array<string> | undefined } :
        undefined,
      config.features.beaconChain.isEnabled && addressTabsCountersQuery.data?.withdrawals_count ?
        {
          id: 'withdrawals',
          title: t(ADDRESS_TAB_KEYS.withdrawals),
          count: addressTabsCountersQuery.data?.withdrawals_count,
          subTabs: undefined as Array<string> | undefined,
        } :
        undefined,
      {
        id: 'token_transfers',
        title: t(ADDRESS_TAB_KEYS.tokenTransfers),
        count: addressTabsCountersQuery.data?.token_transfers_count,
        subTabs: undefined as Array<string> | undefined,
      },
      {
        id: 'tokens',
        title: t(ADDRESS_TAB_KEYS.tokens),
        count: addressTabsCountersQuery.data?.token_balances_count,
        subTabs: TOKEN_TABS as Array<string> | undefined,
      },
      {
        id: 'internal_txns',
        title: t(ADDRESS_TAB_KEYS.internalTxns),
        count: addressTabsCountersQuery.data?.internal_transactions_count,
        subTabs: undefined as Array<string> | undefined,
      },
      addressTabsCountersQuery.data?.celo_election_rewards_count ? {
        id: 'epoch_rewards',
        title: t(ADDRESS_TAB_KEYS.epochRewards),
        count: addressTabsCountersQuery.data?.celo_election_rewards_count,
        subTabs: undefined as Array<string> | undefined,
      } : undefined,
      {
        id: 'coin_balance_history',
        title: t(ADDRESS_TAB_KEYS.coinBalanceHistory),
        count: undefined,
        subTabs: undefined as Array<string> | undefined,
      },
      addressTabsCountersQuery.data?.validations_count ?
        {
          id: 'blocks_validated',
          title: t(ADDRESS_TAB_KEYS.blocksValidated, { networkValidationActionText: getNetworkValidationActionText() }),
          count: addressTabsCountersQuery.data?.validations_count,
          subTabs: undefined as Array<string> | undefined,
        } :
        undefined,
      addressTabsCountersQuery.data?.logs_count ?
        {
          id: 'logs',
          title: t(ADDRESS_TAB_KEYS.logs),
          count: addressTabsCountersQuery.data?.logs_count,
          subTabs: undefined as Array<string> | undefined,
        } :
        undefined,
      addressQuery.data?.is_contract ? {
        id: 'contract',
        title: addressQuery.data.is_verified ? `${ t(ADDRESS_TAB_KEYS.contract) } \u2713` : t(ADDRESS_TAB_KEYS.contract),
        count: undefined,
        subTabs: CONTRACT_TAB_IDS as Array<string> | undefined,
      } : undefined,
    ].filter(Boolean) as Array<{ id: string; title: string; count?: number; subTabs?: Array<string> }>;
  }, [
    addressQuery.data,
    addressTabsCountersQuery.data,
    userOpsAccountQuery.data,
    mudTablesCountQuery.data,
    t,
  ]);

  const tab = getQueryParamString(router.query.tab);
  const activeTab = React.useMemo(() => {
    if (!tab) {
      return tabs[0]?.id || 'txs';
    }
    const found = tabs.find(({ id, subTabs }) => id === tab || subTabs?.includes(tab));
    return found?.id || tabs[0]?.id || 'txs';
  }, [ tab, tabs ]);

  const handleTabClick = React.useCallback((tabId: string) => () => {
    const queryForPathname = pickBy(router.query, (value, key) => router.pathname.includes(`[${ key }]`));
    React.startTransition(() => {
      router.push(
        { pathname: router.pathname, query: { ...queryForPathname, tab: tabId } },
        undefined,
        { shallow: true },
      );
    });
  }, [ router ]);

  const renderTabContent = () => {
    if (addressQuery.isError || addressQuery.isDegradedData) {
      return null;
    }

    switch (activeTab) {
      case 'mud':
        return <AddressMud scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'txs':
        return <AddressTxs scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'account_history':
        return <AddressAccountHistory scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'user_ops':
        return <AddressUserOps shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'withdrawals':
        return <AddressWithdrawals scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'token_transfers':
        return <AddressTokenTransfers scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'tokens':
        return <AddressTokens shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'internal_txns':
        return <AddressInternalTxs scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'epoch_rewards':
        return <AddressEpochRewards scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'coin_balance_history':
        return <AddressCoinBalance shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'blocks_validated':
        return <AddressBlocksValidated scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'logs':
        return <AddressLogs scrollRef={ tabsScrollRef } shouldRender={ !isTabsLoading } isQueryEnabled={ areQueriesEnabled }/>;
      case 'contract':
        return (
          <AddressContract
            tabs={ contractTabs.tabs }
            shouldRender={ !isTabsLoading }
            isLoading={ contractTabs.isLoading }
          />
        );
      default:
        return null;
    }
  };

  const usernameApiTag = userPropfileApiQuery.data?.user_profile?.username;

  const tags: Array<EntityTag> = React.useMemo(() => {
    return [
      ...(addressQuery.data?.public_tags?.map((tag) => ({ slug: tag.label, name: tag.display_name, tagType: 'custom' as const, ordinal: -1 })) || []),
      !addressQuery.data?.is_contract ?
        { slug: 'eoa', name: t(ADDRESS_TAG_KEYS.eoa), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } :
        undefined,
      config.features.validators.isEnabled && addressQuery.data?.has_validated_blocks ?
        { slug: 'validator', name: t(ADDRESS_TAG_KEYS.validator), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } :
        undefined,
      addressQuery.data?.implementations?.length ?
        { slug: 'proxy', name: t(ADDRESS_TAG_KEYS.proxy), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } :
        undefined,
      addressQuery.data?.token ? { slug: 'token', name: t(ADDRESS_TAG_KEYS.token), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } : undefined,
      isSafeAddress ? { slug: 'safe', name: t(ADDRESS_TAG_KEYS.multisigSafe), tagType: 'custom' as const, ordinal: -10 } : undefined,
      addressProfileAPIFeature.isEnabled && usernameApiTag ? {
        slug: 'username_api',
        name: usernameApiTag,
        tagType: 'custom' as const,
        ordinal: 11,
        meta: {
          tagIcon: addressProfileAPIFeature.tagIcon,
          bgColor: addressProfileAPIFeature.tagBgColor,
          textColor: addressProfileAPIFeature.tagTextColor,
          tagUrl: addressProfileAPIFeature.tagLinkTemplate ? addressProfileAPIFeature.tagLinkTemplate.replace('{username}', usernameApiTag) : undefined,
        },
      } : undefined,
      config.features.userOps.isEnabled && userOpsAccountQuery.data ?
        { slug: 'user_ops_acc', name: t(ADDRESS_TAG_KEYS.smartContractWallet), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } :
        undefined,
      config.features.mudFramework.isEnabled && mudTablesCountQuery.data ?
        { slug: 'mud', name: t(ADDRESS_TAG_KEYS.mudWorld), tagType: 'custom' as const, ordinal: PREDEFINED_TAG_PRIORITY } :
        undefined,
      ...formatUserTags(addressQuery.data),
      ...(addressMetadataQuery.data?.addresses?.[hash.toLowerCase()]?.tags.filter(tag => tag.tagType !== 'note') || []),
      !addressQuery.data?.is_contract && xScoreFeature.isEnabled && xStarQuery.data?.data.level ?
        {
          slug: 'xstar',
          name: t(ADDRESS_TAG_KEYS.xstarLevel, { level: xStarQuery.data.data.level }),
          tagType: 'custom' as const,
          ordinal: 12,
          meta: {
            tooltipTitle: t(ADDRESS_TAG_KEYS.xstarTooltipTitle),
            tooltipDescription:
              t(ADDRESS_TAG_KEYS.xstarTooltipDescription),
            tooltipUrl: xScoreFeature.url,
          },
        } :
        undefined,
    ].filter(Boolean).sort(sortEntityTags);
  }, [
    addressMetadataQuery.data,
    addressQuery.data,
    hash,
    isSafeAddress,
    userOpsAccountQuery.data,
    mudTablesCountQuery.data,
    usernameApiTag,
    xStarQuery.data?.data,
    t,
  ]);

  const pageBg = useColorModeValue('#FBFCFE', '#171717');
  const primaryText = useColorModeValue('#000000', '#FFFFFF');
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const activeBg = '#14DCAC';
  const activeText = useColorModeValue('#FFFFFF', '#000000');
  const mutedText = useColorModeValue('#616161', '#999999');

  const tagsElement = (
    <EntityTags
      tags={ tags }
      isLoading={
        isLoading ||
        (config.features.userOps.isEnabled && userOpsAccountQuery.isPlaceholderData) ||
        (config.features.addressMetadata.isEnabled && addressMetadataQuery.isPending) ||
        (addressProfileAPIFeature.isEnabled && userPropfileApiQuery.isPending) ||
        (xScoreFeature.isEnabled && xStarQuery.isPlaceholderData)
      }
    />
  );

  return (
    <Box px={{ base: 4, lg: 6 }} pb={{ base: 6, lg: 8 }} bg={ pageBg }>
      <Flex
        mb="24px"
        alignItems="center"
        justifyContent="space-between"
        gap={ 3 }
        flexWrap="wrap"
      >
        <Text
          as="h1"
          color={ primaryText }
          fontSize="24px"
          lineHeight="31px"
          fontWeight={ 700 }
          fontFamily="'Space Grotesk', var(--chakra-fonts-heading)"
        >
          { addressQuery.data?.is_contract ? t(ADDRESS_PAGE_KEYS.title.contractDetails) : t(ADDRESS_PAGE_KEYS.title.addressDetails) }
        </Text>
        { tagsElement }
      </Flex>

      <Flex alignItems="center" w="100%" columnGap={ 2 } rowGap={ 2 } flexWrap={{ base: 'wrap', lg: 'nowrap' }} mb="16px">
        { addressQuery.data?.ens_domain_name && (
          <EnsEntity
            domain={ addressQuery.data?.ens_domain_name }
            protocol={ !addressEnsDomainsQuery.isPending ? addressMainDomain?.protocol : null }
            fontFamily="heading"
            fontSize="lg"
            fontWeight={ 500 }
            mr={ 1 }
            maxW="300px"
          />
        ) }
        <AddressEntity
          address={{
            ...addressQuery.data,
            hash: checkSummedHash,
            name: '',
            ens_domain_name: '',
            implementations: null,
          }}
          isLoading={ isLoading }
          fontFamily="heading"
          fontSize="lg"
          fontWeight={ 500 }
          noLink
          noCopy
          noIcon
          isSafeAddress={ isSafeAddress }
          icon={{ color: isSafeAddress ? safeIconColor : undefined }}
        />
        <AddressCopyButton text={ addressQuery.data?.filecoin?.robust ?? checkSummedHash } isLoading={ isLoading }/>
        <AddressQrCode address={{ hash: addressQuery.data?.filecoin?.robust ?? checkSummedHash }} isLoading={ isLoading }/>
        { !isLoading && addressQuery.data?.is_contract && addressQuery.data.token &&
          <AddressAddToWallet token={ addressQuery.data.token } variant="button"/> }
        { !isLoading && !addressQuery.data?.is_contract && config.features.account.isEnabled && (
          <AddressFavoriteButton hash={ hash } watchListId={ addressQuery.data?.watchlist_address_id }/>
        ) }
        <AccountActionsMenu isLoading={ isLoading }/>
        <HStack ml="auto" gap={ 2 }/>
        { !isLoading && addressQuery.data?.is_contract && addressQuery.data?.is_verified && config.UI.views.address.solidityscanEnabled &&
          <SolidityscanReport hash={ hash }/> }
        { !isLoading && addressEnsDomainsQuery.data && config.features.nameService.isEnabled &&
          <AddressEnsDomains query={ addressEnsDomainsQuery } addressHash={ hash } mainDomainName={ addressQuery.data?.ens_domain_name }/> }
        <NetworkExplorers type="address" pathParam={ hash.toLowerCase() }/>
      </Flex>

      { !addressMetadataQuery.isPending &&
        <AddressMetadataAlert tags={ addressMetadataQuery.data?.addresses?.[hash.toLowerCase()]?.tags } mt="8px" mb={ 6 }/> }
      { config.features.metasuites.isEnabled && <Box display="none" id="meta-suites__address" data-ready={ !isLoading }/> }
      <AddressDetails addressQuery={ addressQuery } scrollRef={ tabsScrollRef }/>
      { /* should stay before tabs to scroll up with pagination */ }
      <Box ref={ tabsScrollRef }></Box>

      <Flex mb="16px" gap="8px" flexWrap="wrap">
        { tabs.map((item) => {
          const isActive = item.id === activeTab;
          let countLabel: string | undefined;
          if (item.count !== undefined) {
            countLabel = item.count > 50 ? '50+' : item.count.toLocaleString();
          }
          const label = countLabel !== undefined ? `${ item.title }(${ countLabel })` : item.title;

          return (
            <Button
              key={ item.id }
              type="button"
              onClick={ handleTabClick(item.id) }
              h="40px"
              minW="auto"
              px="16px"
              borderRadius="4px"
              border="1px solid"
              borderColor={ borderColor }
              bg={ isActive ? activeBg : cardBg }
              color={ isActive ? activeText : mutedText }
              fontSize="14px"
              lineHeight="22px"
              fontWeight={ 500 }
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              _hover={{
                bg: isActive ? activeBg : cardBg,
                color: isActive ? activeText : primaryText,
              }}
              _active={{
                bg: isActive ? activeBg : cardBg,
              }}
            >
              { label }
            </Button>
          );
        }) }
      </Flex>

      <Box overflow="visible">
        { renderTabContent() }
      </Box>
    </Box>
  );
};

export default AddressPageContent;
